import { z } from 'zod';
import type { Permission } from '../permissions';
import { idParamSchema, optionalString, paginationQuerySchema, requiredString } from './common';
import { type RequisitionStatus, requisitionStatusSchema } from './enums';

/**
 * Buying reagent: what to order, and what turned up.
 *
 * Packs everywhere, never base units. A purchase order is a conversation with a
 * supplier, and no supplier has an opinion about microlitres — the conversion
 * happens at receipt, where the pack size that actually arrived is known.
 */

/** Whole packs. Ordering "2.5 kits" is not something a supplier can fulfil. */
const packs = z.coerce.number().int().min(1).max(100_000);

const requisitionLineSchema = z.object({
  item_id: idParamSchema,
  quantity_packs: packs,
  /** Price per pack as quoted. Absent until somebody asks the supplier. */
  unit_price: z.coerce.number().min(0).max(99_999_999).multipleOf(0.01).nullish(),
  note: optionalString(),
});

export const createRequisitionSchema = z.object({
  supplier_id: idParamSchema.nullish(),
  department_id: idParamSchema.nullish(),
  expected_at: z.iso.date().nullish(),
  note: optionalString(),
  lines: z
    .array(requisitionLineSchema)
    .min(1, 'Add at least one reagent')
    .max(200)
    // Caught here rather than at the unique index, so the operator gets a field
    // error on a form they can still fix instead of a 409 after saving.
    .refine(
      (lines) => new Set(lines.map((line) => line.item_id)).size === lines.length,
      'The same reagent is on this request twice — change the quantity instead',
    ),
});
export type CreateRequisitionInput = z.infer<typeof createRequisitionSchema>;
export const updateRequisitionSchema = createRequisitionSchema;
export type UpdateRequisitionInput = z.infer<typeof updateRequisitionSchema>;

/**
 * Moving a requisition along the ladder.
 *
 * `expected` is the status the client believes it is in, and the server
 * compare-and-sets on it. Sending it is what turns "two people approved at
 * once" into one success and one honest 409 rather than two approvals.
 */
export const requisitionTransitionSchema = z
  .object({
    to: requisitionStatusSchema,
    expected: requisitionStatusSchema,
    reason: optionalString(),
    expected_at: z.iso.date().nullish(),
  })
  .refine(
    (value) => value.to !== 'rejected' || (value.reason ?? '').trim().length > 0,
    'Say why it is being rejected',
  );
export type RequisitionTransitionInput = z.infer<typeof requisitionTransitionSchema>;

/**
 * The permission each requested transition needs. The two receipt states are
 * reached by recording a delivery, not by this endpoint, and moving back to
 * draft is a correction — both ride on the strongest of the five.
 */
export const REQUISITION_TRANSITION_PERMISSION = {
  submitted: 'requisition.submit',
  cancelled: 'requisition.cancel',
  approved: 'requisition.approve',
  rejected: 'requisition.reject',
  ordered: 'requisition.order',
  draft: 'requisition.approve',
  partially_received: 'requisition.approve',
  received: 'requisition.approve',
} as const satisfies Record<RequisitionStatus, Permission>;

export const requisitionQuerySchema = paginationQuerySchema.extend({
  status: requisitionStatusSchema.optional(),
  supplier_id: idParamSchema.optional(),
  department_id: idParamSchema.optional(),
  /** Everything still in play — the inbox's default view. */
  open: z.enum(['true', 'false']).optional(),
});
export type RequisitionQuery = z.infer<typeof requisitionQuerySchema>;

/* ------------------------------------------------------------------------- *
 * Receiving
 * ------------------------------------------------------------------------- */

/**
 * One reagent on a delivery.
 *
 * `lot_no`, `expiry_at` and `pack_size_base` are properties of the delivery, not
 * of the order: the vendor ships whichever lot is on their shelf, and kit sizes
 * change between orders.
 */
const receiptLineSchema = z.object({
  item_id: idParamSchema,
  quantity_packs: packs,
  /** `—` is a legitimate value for a consumable with no vendor batch. */
  lot_no: requiredString('Lot number'),
  expiry_at: z.iso.date().nullish(),
  /** Defaults to the item's own pack size when the delivery matched the order. */
  pack_size_base: z.coerce.number().min(0.0001).max(9_999_999_999).multipleOf(0.0001).optional(),
  pack_price: z.coerce.number().min(0).max(99_999_999).multipleOf(0.01).default(0),
});
export type ReceiptLineInput = z.infer<typeof receiptLineSchema>;

export const createReceiptSchema = z.object({
  received_at: z.iso.date().nullish(),
  invoice_no: optionalString(),
  delivery_note: optionalString(),
  note: optionalString(),
  lines: z.array(receiptLineSchema).min(1, 'Record at least one reagent').max(200),
});
export type CreateReceiptInput = z.infer<typeof createReceiptSchema>;

/**
 * Build a draft from everything at or below its reorder level.
 *
 * This is the "Please reorder the Hep C kit" column of their Inventory Detail
 * sheet, turned into something that can be acted on.
 */
export const suggestRequisitionSchema = z.object({
  department_id: idParamSchema.nullish(),
  supplier_id: idParamSchema.nullish(),
  /**
   * How many packs to ask for per reagent.
   *
   * A flat number rather than a computed "economic order quantity": the lab has
   * no lead-time or consumption-rate data to compute one from, and inventing a
   * formula would put a number on a purchase order that nobody chose.
   */
  packs_each: z.coerce.number().int().min(1).max(100).default(1),
});
export type SuggestRequisitionInput = z.infer<typeof suggestRequisitionSchema>;
